import { computed } from '@ember/object';
import { isNone, isPresent } from '@ember/utils';
import Money from '@square/dashboard-data/models/squareup/common/money-proto';
import { attr, ProtoBaseModel, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
import MoneyModel from '@square/dashboard-money/models/money';
import { UsageUnitType, normedUnitTypeFor } from '@square/dashboard-data/models/squareup/sub2/usage';
export const PricingTier = ProtoBaseModel.extend({
  cost: attr(Money),
  maxUsageCount: attr(Number),
  minUsageCount: attr(Number),
  name: attr(String)
});
export const UsageBucket = ProtoBaseModel.extend({
  cost: attr(Money),
  unitType: attr(UsageUnitType),
  size: attr(Number),
  label: attr(String),
  normedUnitType: computed('unitType', function () {
    return normedUnitTypeFor(this.unitType);
  })
});
export const FixedPricing = ProtoBaseModel.extend({
  cost: attr(Money),
  perUnitCost() {
    return MoneyModel.zero();
  },
  costForUsage() {
    return this.cost;
  }
});
export const BucketPricing = ProtoBaseModel.extend({
  cost: attr(Money),
  unitType: attr(UsageUnitType),
  bucketSize: attr(Number),
  baseCost: attr(Money),
  perUnitCost(unitType) {
    const normedUnitType = normedUnitTypeFor(unitType);
    const normedBucketUnitType = normedUnitTypeFor(this.unitType);
    if (normedUnitType === normedBucketUnitType) {
      return this.cost;
    } else {
      return MoneyModel.zero();
    }
  },
  costForUsage(usage) {
    const normedUnitType = normedUnitTypeFor(this.unitType);
    const unitUsage = usage.get('unitUsages').findBy('normedUnitType', normedUnitType);
    let count;
    if (!isNone(unitUsage)) {
      count = unitUsage.get('count');
    } else {
      count = 0;
    }
    return this.cost.multiply(count);
  }
});
export const MultiBucketPricing = ProtoBaseModel.extend({
  buckets: repeated(UsageBucket),
  baseCost: attr(Money),
  perUnitCost(unitType) {
    const normedUnitType = normedUnitTypeFor(unitType);
    const bucketForUnitType = this.buckets.findBy('normedUnitType', normedUnitType);
    if (!isNone(bucketForUnitType)) {
      return bucketForUnitType.get('cost');
    } else {
      return MoneyModel.zero();
    }
  },
  costForUsage(usage) {
    return this.buckets.reduce(function (acc, value) {
      const normedUnitType = normedUnitTypeFor(value.unitType);
      const unitUsage = usage.get('unitUsages').findBy('normedUnitType', normedUnitType);
      let count;
      if (!isNone(unitUsage)) {
        count = unitUsage.get('count');
      } else {
        count = 0;
      }
      return acc.add(value.get('cost').multiply(count));
    }, this.baseCost === undefined ? MoneyModel.zero() : this.baseCost);
  }
});
export const TieredPricing = ProtoBaseModel.extend({
  pricingTier: repeated(PricingTier),
  unitType: attr(UsageUnitType),
  tierForUsage(usage) {
    const normedUnitType = normedUnitTypeFor(this.unitType);
    const unitUsage = usage.get('unitUsages').findBy('normedUnitType', normedUnitType);
    let count;
    if (!isNone(unitUsage)) {
      count = unitUsage.get('count');
    } else {
      count = 0;
    }
    return this.pricingTier.find(tier => {
      const minUsageCount = tier.get('minUsageCount') || 0;
      const maxUsageCount = tier.get('maxUsageCount') || Number.POSITIVE_INFINITY;
      return minUsageCount <= count && count <= maxUsageCount;
    });
  },
  perUnitCost() {
    return MoneyModel.zero();
  },
  costForUsage(usage) {
    const pricingTier = this.tierForUsage(usage);
    return pricingTier.get('cost');
  }
});
export const RetailPricing = ProtoBaseModel.extend({
  retailDeviceCost: attr(Money),
  excessEmployeeCost: attr(Money),
  excessCustomerBucketCost: attr(Money),
  perUnitCost() {
    return MoneyModel.zero();
  },
  costForUsage() {
    return MoneyModel.zero();
  }
});
export default ProtoBaseModel.extend({
  label: attr(String),
  customPricing: attr(Boolean),
  // Pricing proto will return one of the following
  fixedPricing: attr(FixedPricing),
  bucketPricing: attr(BucketPricing),
  multiBucketPricing: attr(MultiBucketPricing),
  tieredPricing: attr(TieredPricing),
  retailPricing: attr(RetailPricing),
  // deprecated pricing type

  pricingInfo: computed('fixedPricing', 'bucketPricing', 'multiBucketPricing', 'tieredPricing', 'retailPricing', function () {
    const pricingTypes = ['fixedPricing', 'bucketPricing', 'multiBucketPricing', 'tieredPricing', 'retailPricing'];
    for (const pricingType of pricingTypes) {
      const info = this.get(pricingType);
      if (isPresent(info)) {
        return info;
      }
    }
    return null;
  }),
  formattedPriceForUnit(unitType) {
    const perUnitCost = this.pricingInfo.perUnitCost(unitType);
    return perUnitCost.get('formattedShort');
  },
  formattedCost(usage) {
    // If adding a new pricing type, make sure to update this code
    if ((isNone(usage) || isNone(usage.get('unitUsages'))) && isNone(this.fixedPricing)) {
      return null;
    } else {
      const cost = this.pricingInfo.costForUsage(usage);
      return cost.get('formattedShort');
    }
  },
  dependsOnUnitType(unitType) {
    // If adding a new pricing type that depends on unit type, make sure to update all code
    // that currently depends on pricing for unit types (strings)
    // Ex: Reactivate Employee modal
    if (this.bucketPricing) {
      return this.get('bucketPricing.unitType') === unitType;
    } else if (this.multiBucketPricing) {
      return this.get('multiBucketPricing.buckets').mapBy('unitType').includes(unitType);
    } else if (this.tieredPricing) {
      return this.get('tieredPricing.unitType') === unitType;
    }
    return false;
  }
});